import {
  AddressResponse,
  AppointmentGroupingResponse,
  LocationType,
  ProjectResponse,
  TimeWindowResponse,
  WorkPlaceResponse
} from 'generated';
import { useTranslation } from 'react-i18next';

import { formatByLocale, formatDateTime, formatHoursMinutes } from 'utils/date';

import { Table, TableBody, TableCell, TableRow } from '@mui/material';
import { useRecoilValue } from 'recoil';
import { dialogActiveAtom } from '../../atoms';

type Props = {
  clientName?: string;
  employeeName?: string;
  activityName?: string | undefined;
  employeeTypeName?: string | undefined;
  start?: string;
  project?: ProjectResponse;
  workPlace?: WorkPlaceResponse;
  appointmentGrouping?: AppointmentGroupingResponse;
  durationInSeconds: number;
  distanceInMeters?: number;
  recurrenceRule?: string;
  timeWindow?: TimeWindowResponse;
  locationType?: LocationType;
  address?: AddressResponse;
  noteInternal?: string;
  phoneNumbers?: string[] | null;
  keyArrangements?: string[] | null;
  planningFeatures?: string[] | null;
  absenceReason?: string;
  warnings?: string[];
  onMouseOver?: () => void;
  isStaticHTMLRender?: boolean;
  clientNumber?: number;
  createdOn?: string | null;
  createdByName?: string | null;
  lastUpdatedOn?: string | null;
  lastUpdatedByName?: string | null;
};

function ToolTipList({ values = [] }: { values: string[] }) {
  return (
    <ul
      style={{
        listStyleType: 'none'
      }}
    >
      {values.map((value, idx) => (
        <li key={idx}>{value}</li>
      ))}
    </ul>
  );
}

export default function EventTooltipBody({
  clientName,
  employeeName,
  activityName,
  employeeTypeName,
  project,
  workPlace,
  appointmentGrouping,
  start,
  durationInSeconds,
  distanceInMeters,
  recurrenceRule,
  timeWindow,
  locationType,
  address,
  noteInternal,
  phoneNumbers,
  keyArrangements,
  planningFeatures,
  absenceReason,
  warnings,
  onMouseOver,
  isStaticHTMLRender,
  clientNumber,
  createdOn,
  createdByName,
  lastUpdatedOn,
  lastUpdatedByName
}: Props) {
  const { t } = useTranslation('common');
  const dialogActive = isStaticHTMLRender
    ? false
    : useRecoilValue(dialogActiveAtom);

  if (dialogActive) return null;

  return (
    <Table
      onMouseOver={onMouseOver}
      size="small"
      sx={{
        'tr:last-child td': { border: 0 },
        th: { fontFamily: 'SourceSansPro-Regular' },
        'tr td:first-of-type': { fontFamily: 'SourceSansPro-Bold' }
      }}
    >
      <TableBody>
        {clientName && (
          <TableRow>
            <TableCell colSpan={2}>{t('client')}</TableCell>
            <TableCell>{clientName}</TableCell>
          </TableRow>
        )}
        {clientNumber && (
          <TableRow>
            <TableCell colSpan={2}>{t('clientNumber')}</TableCell>
            <TableCell>{clientNumber}</TableCell>
          </TableRow>
        )}
        {employeeName && (
          <TableRow>
            <TableCell colSpan={2}>{t('employee')}</TableCell>
            <TableCell>{employeeName}</TableCell>
          </TableRow>
        )}
        {appointmentGrouping && (
          <TableRow>
            <TableCell colSpan={2}>{t('activityPlanning.route')}</TableCell>
            <TableCell>{appointmentGrouping.description}</TableCell>
          </TableRow>
        )}
        {project && (
          <TableRow>
            <TableCell colSpan={2}>{t('activityPlanning.project')}</TableCell>
            <TableCell>{project.name}</TableCell>
          </TableRow>
        )}
        {workPlace && (
          <TableRow>
            <TableCell colSpan={2}>{t('activityPlanning.workPlace')}</TableCell>
            <TableCell>{workPlace.name}</TableCell>
          </TableRow>
        )}
        {activityName && (
          <TableRow>
            <TableCell colSpan={2}>{t('activity')}</TableCell>
            <TableCell>{activityName}</TableCell>
          </TableRow>
        )}
        {employeeTypeName && (
          <TableRow>
            <TableCell colSpan={2}>{t('activityPlanning.expertise')}</TableCell>
            <TableCell>{employeeTypeName}</TableCell>
          </TableRow>
        )}
        {start && !project && (
          <TableRow>
            <TableCell colSpan={2}>{t('activityPlanning.start')}</TableCell>
            <TableCell>{formatByLocale(new Date(start), 'HH:mm')}</TableCell>
          </TableRow>
        )}
        <TableRow>
          <TableCell colSpan={2}>{t('duration')}</TableCell>
          <TableCell>{formatHoursMinutes(durationInSeconds)}</TableCell>
        </TableRow>
        {distanceInMeters && (
          <TableRow>
            <TableCell colSpan={2}>{t('activityPlanning.distance')}</TableCell>
            <TableCell>{(distanceInMeters / 1000).toFixed(1)} km</TableCell>
          </TableRow>
        )}
        {recurrenceRule && (
          <TableRow>
            <TableCell colSpan={2}>{t('recurrenceRule')}</TableCell>
            <TableCell>{recurrenceRule}</TableCell>
          </TableRow>
        )}
        {timeWindow && (
          <TableRow>
            <TableCell colSpan={2}>
              {t('activityPlanning.timeWindow')}
            </TableCell>
            <TableCell>{`${formatByLocale(
              new Date(timeWindow.start),
              'HH:mm'
            )} - ${formatByLocale(new Date(timeWindow.end), 'HH:mm')} ${
              timeWindow.isUrgent
                ? t('activityPlanning.urgent').toUpperCase()
                : ''
            }`}</TableCell>
          </TableRow>
        )}
        {locationType && clientName && (
          <TableRow>
            <TableCell colSpan={2}>{t('activityPlanning.location')}</TableCell>
            <TableCell>
              {locationType == 'TELECARE'
                ? t('activityPlanning.telecare')
                : address
                ? `${address?.streetName} ${address.houseNumber}, ${address.postalCode} ${address.city}`
                : ''}
            </TableCell>
          </TableRow>
        )}
        {absenceReason && (
          <TableRow>
            <TableCell colSpan={2}>Reden afwezigheid</TableCell>
            <TableCell>{absenceReason}</TableCell>
          </TableRow>
        )}
        {noteInternal && (
          <TableRow>
            <TableCell colSpan={2}>Opmerking</TableCell>
            <TableCell>{noteInternal}</TableCell>
          </TableRow>
        )}
        {phoneNumbers && (
          <TableRow>
            <TableCell
              colSpan={2}
              sx={{
                verticalAlign: 'top'
              }}
            >
              Telefoonnummers
            </TableCell>
            <TableCell
              sx={{
                verticalAlign: 'top'
              }}
            >
              <ToolTipList values={phoneNumbers} />
            </TableCell>
          </TableRow>
        )}
        {keyArrangements && (
          <TableRow>
            <TableCell
              colSpan={2}
              sx={{
                verticalAlign: 'top'
              }}
            >
              Sleutelregeling
            </TableCell>
            <TableCell
              sx={{
                verticalAlign: 'top'
              }}
            >
              <ToolTipList values={keyArrangements} />
            </TableCell>
          </TableRow>
        )}
        {planningFeatures && (
          <TableRow>
            <TableCell
              colSpan={2}
              sx={{
                verticalAlign: 'top'
              }}
            >
              Planningskenmerken
            </TableCell>

            <TableCell
              sx={{
                verticalAlign: 'top'
              }}
            >
              <ToolTipList values={planningFeatures} />
            </TableCell>
          </TableRow>
        )}
        {warnings && warnings.length > 0 && (
          <TableRow>
            <TableCell
              colSpan={2}
              sx={{ color: 'warning.main', verticalAlign: 'top' }}
            >
              {t('activityPlanning.warning')}
            </TableCell>
            <TableCell
              sx={{
                verticalAlign: 'top'
              }}
            >
              <ToolTipList values={warnings} />
            </TableCell>
          </TableRow>
        )}
        {(createdOn || createdByName || lastUpdatedOn || lastUpdatedByName) && (
          <TableRow>
            <TableCell colSpan={2} sx={{ verticalAlign: 'top' }}>
              {t('mrp.appointmentForm.audit')}
            </TableCell>
            <TableCell sx={{ verticalAlign: 'top' }}>
              {(createdOn || createdByName) && (
                <div>
                  {t('mrp.appointmentForm.createdOn')}:{' '}
                  {formatDateTime(createdOn ?? undefined)}
                  {createdByName != null && ` (${createdByName})`}
                </div>
              )}
              {(lastUpdatedOn || lastUpdatedByName) && (
                <div>
                  {t('mrp.appointmentForm.lastUpdatedOn')}:{' '}
                  {formatDateTime(lastUpdatedOn ?? undefined)}
                  {lastUpdatedByName != null && ` (${lastUpdatedByName})`}
                </div>
              )}
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
}
